import { axios } from '@boot/axios.js';
import { i18n } from '@boot/i18n.js';
// Status refresh using sensors
import { DEVICE_STATUS } from '@models/devices.js';
import { useSharedStore } from '@stores/shared.js';

/**
 * Reset store state.
 */
export function resetState({ commit }) {
    commit('RESET_STATE');
}

/**************
 * FACILITIES *
 *************/

/**
 * Load facilities from server and set them into the store.
 */
export function loadFacilities({ commit }) {
    return new Promise((resolve, reject) => {
        axios
            .get('facilities/')
            .then(response => {
                const facilities = response.data.results;
                commit('ADD_FACILITIES', facilities);
                resolve(response);
            })
            .catch(error => {
                console.error('Failed to load facilities due to: ', error);
                reject(error);
            });
    });
}

/**
 * Load facility groups from server and set them into the store.
 * @param {Object} context - The Vuex action context.
 * @param {Function} context.commit - The Vuex commit function.
 * @returns {Promise} A promise that resolves with the response or rejects with an error.
 */
export function loadFacilityGroups({ commit }) {
    return new Promise((resolve, reject) => {
        axios
            .get('facility_groups/')
            .then(response => {
                const rawData = response.data.results;
                const facilityGroups = rawData.map(group => ({
                    id: group.id,
                    name: group.name,
                    facilities: group.facilities
                }));
                commit('ADD_FACILITY_GROUPS', facilityGroups);
                resolve(facilityGroups);
            })
            .catch(error => {
                console.error('Failed to load facility groups due to: ', error);
                reject(error);
            });
    });
}

/**
 * Handles facility update.
 */
export function updateFacility({ commit }, facility) {
    return new Promise((resolve, reject) => {
        axios
            .put(`facilities/${facility.id}/`, facility)
            .then(response => {
                commit('UPDATE_FACILITY', facility);
                resolve(response);
            })
            .catch(error => {
                console.error('Failed to update facility due to: ', error);
                reject(error);
            });
    });
}

/**
 * Selects the current facility that is active for the store.
 * @param {Number} faciltyId - Facility ID.
 */
export function selectFacility({ commit }, faciltyId) {
    commit('SET_SELECTED_FACILITY', faciltyId);
}

/**
 * Clear the selected facility.
 */
export function clearSelectedFacility({ commit }) {
    commit('CLEAR_SELECTED_FACILITY');
}

/**
 * Add a facility ID with for a load assets request that has failed.
 * @param {Number} facilityId
 */
export function addLoadingAssetsFailed({ commit }, facilityId) {
    commit('ADD_LOADING_ASSETS_FAILED', parseInt(facilityId));
}

/**
 * Remove a facility ID with for a load assets request that has failed.
 * @param {Number} facilityId
 */
export function removeLoadingAssetsFailed({ commit }, facilityId) {
    commit('REMOVE_LOADING_ASSETS_FAILED', parseInt(facilityId));
}

/**********
 * ASSETS *
 **********/

/**
 * Load assets for a specific facility.
 * @param {(string|number)} facilityId - Facility id.
 */
export function loadAssets(
    { commit, dispatch },
    { facilityId, showLoading = true }
) {
    return new Promise((resolve, reject) => {
        // begin loading
        if (showLoading) {
            useSharedStore().beginLoading(
                `${i18n.global.t('Loading assets')}...`
            );
        }

        // load assets
        axios
            .get(`assets/?facility=${facilityId}&with_deleted=true`)
            .then(response => {
                const assets = response.data.results;
                // set assets for this facility
                assets.forEach(asset => {
                    const formattedAsset = formatAsset(asset);
                    commit('ADD_ASSET', formattedAsset);
                });
                // extract the list of groups from the list of assets
                addAssetGroups({ commit }, assets, facilityId);
                removeLoadingAssetsFailed({ commit }, facilityId);
                resolve(response);
            })
            .catch(error => {
                console.error('Failed to load assets due to: ', error);
                reject(error);
            })
            .finally(() => {
                // end loading
                if (showLoading) {
                    useSharedStore().stopLoading();
                }
            });
    });
}

/**
 * Formats a phase object into a standardized asset phase object.
 *
 * @param {Object} phase - The phase object to format.
 * @param {number} phase.id - The ID of the phase.
 * @param {string} phase.uuid - The UUID of the phase.
 * @param {number} phase.asset - The ID of the associated asset.
 * @param {string} phase.phase - The status of the phase.
 * @param {string} phase.cause - The cause of the phase.
 * @param {string} phase.product - The current product associated with the phase.
 * @param {string} phase.product_name - The name of the current product.
 * @param {string} phase.start - The start time of the phase.
 * @param {boolean} phase.locked_transition_to_running - Indicates if the transition to running is locked.
 * @returns {Object} The formatted asset phase object.
 */
function formatAssetPhase(phase) {
    return {
        id: phase.id,
        phaseUUID: phase.uuid,
        assetId: phase.asset,
        status: phase.phase,
        cause: phase.cause,
        currentProduct: phase.product,
        currentProductName: phase.product_name,
        start: phase.start,
        lockedTransitionToRunning: phase.locked_transition_to_running
    };
}

/**
 * Loads the current phases of an asset or facility.
 *
 * @param {Object} context - The Vuex context object.
 * @param {Function} context.commit - The Vuex commit function.
 * @param {Object} payload - The payload object.
 * @param {string|null} [payload.facilityId=null] - The ID of the facility.
 * @param {string|null} [payload.assetId=null] - The ID of the asset.
 * @returns {Promise} A promise that resolves with the response or rejects with an error.
 */
export function loadCurrentPhases(
    { commit },
    { facilityId = null, assetId = null }
) {
    // Check if the facilityId or assetId is provided
    if (!facilityId && !assetId) {
        console.error(
            'Failed to load asset phases due to: ',
            'Either facilityId or assetId must be provided'
        );
        return;
    }

    // Create URL with dynamic query parameters using URLSearchParams
    const params = new URLSearchParams();
    if (facilityId) {
        params.append('facility', facilityId);
    }
    if (assetId) {
        params.append('asset', assetId);
    }

    const url = `/assetphases/current/?${params.toString()}`;

    return new Promise((resolve, reject) => {
        axios
            .get(url)
            .then(response => {
                const phases = response.data.results;
                phases.forEach(phase => {
                    const formattedPhase = formatAssetPhase(phase);
                    // dispatch the setAssetPhase action to set the status of the asset
                    commit('SET_STATUS_ID', {
                        assetId: formattedPhase.assetId
                    });
                    commit('SET_STATUS_VALUE', {
                        assetId: formattedPhase.assetId,
                        status: formattedPhase
                    });
                });
                resolve(response);
            })
            .catch(error => {
                console.error('Failed to load asset phases due to: ', error);
                reject(error);
            });
    });
}

/**
 * Handles asset update.
 */
export function updateAsset({ commit }, asset) {
    return new Promise((resolve, reject) => {
        axios
            .put(`assets/${asset.id}/`, asset)
            .then(response => {
                commit('UPDATE_ASSET', asset);
                resolve(response);
            })
            .catch(error => {
                console.error('Failed to update asset due to: ', error);
                reject(error);
            });
    });
}

/**
 * Select an asset.
 * @param {Number} assetId - Asset ID.
 */
export function selectAsset({ commit }, assetId) {
    commit('SET_SELECTED_ASSET', assetId);
}

/**
 * Set the asset phase.
 * @param {Object} data - Event data
 * @param {Number} data.asset_id - Asset ID
 * @param {String} data.phase - Phase of the asset
 * @param {Number} data.cause_id - Optional cause ID
 * @param {Number} data.product_id - Optional product ID
 */
export function setAssetPhase({ commit }, data) {
    commit('SET_STATUS_ID', { assetId: data['assetId'] });
    commit('SET_STATUS_VALUE', { assetId: data['assetId'], status: data });
}

/**
 * Set current downtime cause for an asset.
 * @param {Number} assetId
 * @param {Number} causeId
 */
export function setDowntimeCause({ commit }, { assetId, causeId }) {
    commit('SET_DOWNTIME_CAUSE', { assetId, causeId });
}

/**
 * Get sensors connected to Asset
 * @param {string} assetId - Asset Id of selected asset.
 * @return {Object} - list of sensors.
 */
export function loadSensors({ commit }, assetId) {
    let sensors = [];
    return new Promise((resolve, reject) => {
        axios
            .get(`/sensors/?asset=${assetId}`)
            .then(response => {
                // set sensors selected
                response.data['results'].forEach(result => {
                    sensors.push({
                        asset: result.asset,
                        device: result.device,
                        id: result.id,
                        location: result.location,
                        sensor_id: result.sensor_id,
                        sensor_type: result.sensor_type,
                        uuid: result.uuid,
                        is_enabled: result.is_enabled
                    });
                });
                commit('ADD_SENSORS', { assetId, sensors });
                resolve({
                    sensors: sensors
                });
            })
            .catch(error => {
                // log error
                console.error('Failed to fetch sensors data due to: ', error);
                reject(error);
            });
    });
}

export function addSensor({ commit }, assetId, sensors) {
    commit('ADD_SENSORS', { assetId, sensors });
}

// Status refresh using sensors
/**
 * Get the status of a device given the device id
 * @param {string} deviceId - Device Id of sensor.
 * @return {string} - status of Device
 */
export async function getDeviceStatus({ commit }, deviceId) {
    return new Promise((resolve, reject) => {
        axios
            .get(`/devices/${deviceId}/status/`)
            .then(response => {
                // set sensor status
                let deviceStatus = DEVICE_STATUS.offline;
                if (response.data && 'is_online' in response.data) {
                    deviceStatus = response.data.is_online
                        ? DEVICE_STATUS.online
                        : DEVICE_STATUS.offline;
                }
                resolve({
                    deviceStatus: deviceStatus
                });
            })
            .catch(error => {
                // log error
                reject(error);
            });
    });
}

/**
 * Load all devices connected to a facility.
 * @param {string} facilityId - Facility Id of selected facility.
 */
export function loadFacilityDevices({ commit }, facilityId) {
    return new Promise((resolve, reject) => {
        axios
            .get(`/devices/?facility=${facilityId}`)
            .then(response => {
                const devices = response.data['results'];

                devices.forEach(device => {
                    if (!device.assets || device.assets.length === 0) {
                        return;
                    }
                    /**
                     * In the facility dashboard the real-time data is hidden if
                     * the asset is not connected to a sensor. This information is
                     * stored in the assets array in the device object.
                     **/
                    commit('ADD_ASSETS_WITH_SENSOR', device.assets);
                });
                resolve();
            })
            .catch(error => {
                // log error
                console.error('Failed to fetch assets data due to: ', error);
                reject(error);
            });
    });
}

/*
 * Load asset groups for a specific facility.
 * @param {Number} assetId
 * @param {Number} causeId
 */
export function addAssetGroups({ commit }, assets, facilityId) {
    let groupMap = {};
    assets.forEach(asset => {
        if (asset.facility === parseInt(facilityId)) {
            for (let group of asset.groups) {
                if (!(group.id in groupMap)) {
                    groupMap[group.id] = {
                        id: group.id,
                        facility: facilityId,
                        name: group.name,
                        assets: [asset.id]
                    };
                } else {
                    groupMap[group.id].assets.push(asset.id);
                }
            }
        }
    });
    Object.values(groupMap).forEach(group => {
        commit('ADD_ASSET_GROUP', group);
    });
}

/**
 * Remove a asset group from store
 * @param {Number} groupId
 */

export function removeAssetGroup({ commit }, groupId) {
    commit('REMOVE_ASSET_GROUP', groupId);
}

/**
 * Add a asset group to store
 * @param {Object} group
 */

export function addAssetGroup({ commit }, group) {
    commit('ADD_ASSET_GROUP', group);
}

/**
 * Update asset group in store
 * @param {Object} group
 */

export function updateAssetGroup({ commit }, group) {
    commit('UPDATE_ASSET_GROUP', group);
}

/**
 * Store state of asset group filter for each facility in store
 * @param {Object} obj
 * @param {Number} obj.facilityId - Facility Id
 * @param {Object} obj.selectedAssetGroup - Asset Group object
 */

export function addSelectedGroupPerFacility(
    { commit },
    { facilityId, selectedAssetGroup }
) {
    commit('ADD_SELECTED_GROUP_PER_FACILITY', {
        facilityId,
        selectedAssetGroup
    });
}

/**
 * Update the state of asset group filter for each facility in store
 * @param {Object} obj
 * @param {Number} obj.facilityId - Facility Id
 * @param {Object} obj.removedAssetGroup - Asset Group object
 */
export function removeSelectedGroupPerFacility(
    { commit },
    { facilityId, removedAssetGroup }
) {
    commit('REMOVE_SELECTED_GROUP_PER_FACILITY', {
        facilityId,
        removedAssetGroup
    });
}

/**
 * Load the current disconnections for the given facility
 * @param {obj} commit
 * @param {Number} obj.facilityId - Facility Id
 */
export function loadDisconnectionStatuses({ commit }, { facilityId }) {
    return new Promise((resolve, reject) => {
        // load device disconnections
        axios
            .get(`disconnections/current/?device__facility=${facilityId}`)
            .then(response => {
                const disconnections = response.data;
                // Empty all the disconnections, since on
                // every call we get the fresh state of
                // active disconnections
                commit('RESET_DISCONNECTIONS');
                // If there are active disconnections
                // populate the vuex state.
                if (disconnections.length > 0) {
                    disconnections.forEach(disconnection => {
                        commit('ADD_DISCONNECTION', {
                            device: disconnection.device,
                            start: disconnection.start,
                            assets: disconnection.assets
                        });
                    });
                }
            })
            .catch(error => {
                console.error('Failed to load disconnections due to: ', error);
                // Stringify the error object
                const errorString = JSON.stringify(error);
                reject(new Error(errorString));
            });
    });
}

/***********
 * DEFECTS *
 ***********/

/**
 * Loads defects for a specific facility and commits them to the store.
 *
 * @param {Object} context - The Vuex action context.
 * @param {Function} context.commit - The Vuex commit function.
 * @param {Object} payload - The payload object.
 * @param {number} payload.facilityId - The ID of the facility to load defects for.
 * @returns {Promise<void>} A promise that resolves when the defects are loaded and committed.
 * @throws Will log an error message if the request fails.
 */
export async function loadFacilityDefects({ commit }, { facilityId }) {
    try {
        const response = await axios.get(`defects/?facility=${facilityId}`);
        const defects = response.data.results;
        defects.forEach(defect => {
            commit('ADD_DEFECT', formatDefect(defect));
        });
    } catch (error) {
        console.error('Failed to load defects due to: ', error);
    }
}

/**
 * Formats a defect object.
 *
 * @param {Object} defect - The defect object to format.
 * @param {number} defect.id - The ID of the defect.
 * @param {string} defect.facility - The facility associated with the defect.
 * @param {string} defect.name - The name of the defect.
 * @param {string} defect.code - The code of the defect.
 * @param {string} defect.description - The description of the defect.
 * @returns {Object} The formatted defect object.
 */
function formatDefect(defect) {
    return {
        id: defect.id,
        facility: defect.facility,
        name: defect.name,
        code: defect.code,
        description: defect.description
    };
}

/**
 * Formats an asset object with specific properties.
 * @returns {Object} The formatted asset object.
 */
function formatAsset(asset) {
    return {
        id: asset.id,
        uuid: asset.uuid,
        facility: asset.facility,
        groups: asset.groups,
        name: asset.name,
        description: asset.description,
        max_downtime_delay: asset.max_downtime_delay,
        max_downtime_justification: asset.max_downtime_justification,
        sensor_configuration: asset.sensor_configuration,
        has_proximity_sensor: asset.has_proximity_sensor,
        proximity_sensor_use: asset.proximity_sensor_use,
        products: asset.products,
        causes: asset.causes,
        dashboard: asset.dashboard,
        speed_widget_color_levels: asset.speed_widget_color_levels,
        use_packaging_factors: asset.use_packaging_factors,
        allow_downtime_dismissal: asset.allow_downtime_dismissal,
        extended_downtime_delay: asset.extended_downtime_delay,
        fault_code_window: asset.fault_code_window,
        is_deleted: asset.is_deleted,
        enable_quality_event_recording: asset.enable_quality_event_recording,
        acceptable_percentage_rejects: asset.acceptable_percentage_rejects,
        acceptable_reject_sample_size: asset.acceptable_reject_sample_size,
        quality_defect_window_s: asset.quality_defect_window_s
    };
}

/******************
 * QUALITY EVENTS *
 *****************/

/**
 * Action to set a quality event.
 *
 * @param {Object} context - The Vuex action context.
 * @param {Function} context.commit - The Vuex commit function.
 * @param {Object} notification - The notification object containing event details.
 * @param {string} notification.eventID - The ID of the event.
 * @param {string} notification.eventUUID - The UUID of the event.
 * @param {string} notification.assetID - The ID of the asset.
 * @param {string} notification.eventDefectID - The ID of the event defect.
 * @param {string} notification.start - The start time of the event.
 * @param {string} notification.end - The end time of the event.
 * @param {string} notification.productID - The ID of the product.
 * @param {number} notification.grossProductCount - The gross production value.
 * @param {number} notification.rejectedProductCount - The rejected production value.
 */
export function setQualityEvent({ commit }, notification) {
    const newEvent = {
        eventID: notification['eventID'],
        eventUUID: notification['eventUUID'],
        assetID: notification['assetID'],
        eventDefectID: notification['eventDefectID'],
        start: notification['start'],
        end: notification['end'],
        productID: notification['productID'],
        grossProductCount: notification['grossProductCount'],
        rejectedProductCount: notification['rejectedProductCount']
    };

    commit('SET_QUALITY_EVENT', newEvent);
}

/**
 * Load quality events for a specific asset.
 * @param {Number} assetId - Asset id.
 * @return {Promise} - Promise that resolves with the server response or rejects in case of an error.
 */
export async function loadQualityEvent({ commit }, assetID) {
    try {
        const response = await axios.get(
            `quality_events/current/?asset=${assetID}`
        );
        const qualityEvents = response.data.results;
        // if the quality event is ongoing, set the ongoing quality event in the store
        if (qualityEvents.length > 0) {
            // ideally, there should only be one ongoing quality event. In case there are multiple, we only consider the first one
            const currentQualityEvent = {
                eventID: qualityEvents[0]['id'],
                eventUUID: qualityEvents[0]['uuid'],
                assetID: qualityEvents[0]['asset'],
                eventDefectID: qualityEvents[0]['defect'],
                start: qualityEvents[0]['start'],
                end: qualityEvents[0]['end'],
                productID: qualityEvents[0]['product'],
                grossProductCount: qualityEvents[0]['gross_production'],
                rejectedProductCount: qualityEvents[0]['rejected_production']
            };

            commit('SET_QUALITY_EVENT', currentQualityEvent);
        } else {
            // if there are no ongoing quality events,
            // remove the current quality event from the store
            commit('REMOVE_QUALITY_EVENT', assetID);
        }
        return response;
    } catch (error) {
        console.error('Failed to load quality events due to: ', error);
        throw error;
    }
}

/**
 * Removes the current quality event.
 *
 * @param {Object} context - The Vuex action context.
 * @param {Function} context.commit - The Vuex commit function.
 * @param {string} assetID - The ID of the asset.
 */
export function removeQualityEvent({ commit }, assetID) {
    commit('REMOVE_QUALITY_EVENT', assetID);
}
